import { useQuery, useSubscription } from "@apollo/client";
import { ProjectContext } from "context/ProjectProvider";
import { useContext, useEffect, useRef, useState } from "react";
import { mergeArrays } from "utils/utils";

export const useSubscriptionStream = (
  queryDef: any,
  subDef: any,
  idField: string,
  params: {
    client: any;
    variables?: any;
    skip?: boolean;
  }
) => {
  const { client, variables, skip = false } = params;

  const [finalData, setFinalData] = useState<any>(null);

  // start with a query
  const {
    data: queryData,
    error: queryError,
    loading: queryLoading,
    refetch: queryRefetch
  } = useQuery(queryDef, {
    fetchPolicy: "cache-and-network",
    variables,
    client,
    skip
  });

  let subVariables = {};
  let queryRespRoot = "";
  if (queryData) {
    const queryRespKeys = Object.keys(queryData);

    queryRespRoot = queryRespKeys[0];

    subVariables = {
      ...variables,
      cursor: {
        initial_value: {
          updated_at: queryData[queryRespRoot][0]?.updated_at
        }
      }
    };
  }

  // then get a subscription
  const {
    data: streamData,
    error: streamError,
    loading: streamLoading
  } = useSubscription(subDef, {
    client,
    variables: subVariables,
    skip: skip || !queryData
  });

  let subRespRoot: string = "";
  if (streamData) {
    const subRespKeys = Object.keys(streamData);

    subRespRoot = subRespKeys[0];
  }

  useEffect(() => {
    if (queryData) {
      setFinalData(queryData[queryRespRoot]);
    }
  }, [queryData, queryRespRoot]);

  useEffect(() => {
    if (queryData && streamData) {
      queryRefetch(); // to update query cache
      const updatedArray = mergeArrays(
        queryData[queryRespRoot],
        streamData[subRespRoot],
        idField
      );

      setFinalData(updatedArray);
    }
  }, [
    idField,
    queryData,
    queryRefetch,
    queryRespRoot,
    streamData,
    subRespRoot
  ]);

  return {
    data: finalData,
    streamError,
    queryError,
    loading: queryLoading || streamLoading
  };
};

export const useQuerySubscription = <TData = any>(
  queryDef: any,
  subDef: any,
  params: {
    client?: any;
    variables?: any;
    skip?: boolean;
  }
): {
  data: TData;
  error: any;
  loading: any;
} => {
  const { client, variables, skip = false } = params;

  const {
    data: queryData,
    error: queryError,
    loading: queryLoading,
    subscribeToMore
  } = useQuery(queryDef, {
    fetchPolicy: "cache-and-network",
    variables,
    client,
    skip
  });

  useEffect(() => {
    if (!queryData) return () => {};
    subscribeToMore({
      document: subDef,
      variables,
      updateQuery: (prev, { subscriptionData }) => {
        if (!subscriptionData.data) return prev;
        return { ...subscriptionData.data };
      }
    });
    return () => {};
  }, [queryData, subDef, subscribeToMore, variables]);

  return {
    data: queryData,
    error: queryError,
    loading: queryLoading
  };
};

export const useCustomOAuthPopup = () => {
  const externalPopup = useRef<any>(null);

  const openPopup = (config: { title: string; url: string }) => {
    const pWidth = 500;
    const pHeight = 500;
    const pLeft = window.screenX + (window.outerWidth - pWidth) / 2;
    const pTop = window.screenY + (window.outerHeight - pHeight) / 2;
    const pTitle = config.title;
    const pUrl = config.url;
    const finalPopup: any = window.open(
      pUrl,
      pTitle,
      `width=${pWidth},height=${pHeight},left=${pLeft},top=${pTop}`
    );
    externalPopup.current = finalPopup;
  };

  const closePopup = () => {
    if (externalPopup.current) {
      externalPopup.current.close();
      externalPopup.current = null;
    }
  };

  const isPopupOpen = externalPopup.current !== null;
  return { openPopup, closePopup, isPopupOpen };
};

export const useShowDesignAndSpec = () => {
  const { isFeatureFlagEnabled } = useContext(ProjectContext);
  const isDesignTabEnabled = isFeatureFlagEnabled("DESIGN_TAB");
  return { isDesignTabEnabled };
};

export function useCloseOnSelector(
  rootRef: React.RefObject<HTMLElement>,
  selector: string,
  onClose: () => void
) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const root = rootRef.current;
    if (!root || !selector) {
      return () => {};
    }

    const handler = (e: Event) => {
      try {
        // Prefer composedPath for portals / shadow DOM
        const path: EventTarget[] =
          typeof (e as any).composedPath === "function"
            ? (e as any).composedPath()
            : // happy-dom Events always provide composedPath; fallback is browser-compat-only
              /* v8 ignore next */
              [e.target];

        // Find the first element in the path that is an Element and can call closest
        const el = path.find(
          (n) => n && (n as Element).closest && (n as Element)
        ) as Element | undefined;

        // If any element in the path matches the selector, trigger close
        const matched = el
          ? el.closest(selector)
          : // happy-dom drives dispatch from composedPath(): the listener only fires
            // when path contains real DOM nodes (all expose .closest), so `el` is
            // always defined and this e.target fallback arm is unreachable in tests
            /* v8 ignore next */
            (e.target as Element).closest?.(selector);
        if (matched) {
          onCloseRef.current();
        }
      } catch (err) {
        // swallow errors to avoid breaking app if composedPath behaves unexpectedly
        // optionally log in dev only
        // import.meta.env.DEV is a constant `true` under Vitest; the non-DEV branch is unreachable in tests
        /* v8 ignore next */
        if (import.meta.env.DEV) {
          console.warn("useCloseOnSelector handler error:", err);
        }
      }
    };

    // Use pointerdown for best responsiveness; include click/touchstart as fallback
    const opts: AddEventListenerOptions = { capture: true, passive: true };
    // root.addEventListener("pointerdown", handler, opts);
    // root.addEventListener("touchstart", handler, opts);
    root.addEventListener("click", handler, opts);

    return () => {
      // root.removeEventListener("pointerdown", handler, opts);
      // root.removeEventListener("touchstart", handler, opts);
      root.removeEventListener("click", handler, opts);
    };
  }, [rootRef, selector]);
}
